/**
 * Unified review data utilities for all review types (set, show, festival)
 * Handles both camelCase and snake_case formats for compatibility
 */

import type { DbSetReview, ReviewData } from "@workspace/types";

// Union type to handle both camelCase and snake_case review formats
type ReviewDataUnion =
	| ReviewData
	| DbSetReview
	| {
			// Set/Show review fields
			rating?: number;
			content?: string | null;
			loved?: boolean;
			sawLive?: boolean;
			saw_live?: boolean;
			sawStream?: boolean;
			saw_stream?: boolean;
			wantToSee?: boolean;
			want_to_see?: boolean;
			mustSee?: boolean;
			must_see?: boolean;
			// Festival review fields
			overallRating?: number;
			vibeRating?: number;
			productionRating?: number;
			logisticsRating?: number;
			foodRating?: number;
	  };

/**
 * Unified review data detection utility
 *
 * Determines if a review has any meaningful content that would warrant keeping it.
 * This replaces inconsistent detection logic across show/set/festival reviews.
 *
 * @param reviewData - Review data in any format (camelCase or snake_case)
 * @returns true if review has meaningful content, false otherwise
 */
export function hasReviewData(
	reviewData: ReviewDataUnion | null | undefined,
): boolean {
	if (!reviewData) return false;

	// Check for festival review fields
	if ('overallRating' in reviewData || 'vibeRating' in reviewData) {
		return (
			(reviewData.overallRating ?? 0) > 0 ||
			(reviewData.vibeRating ?? 0) > 0 ||
			(reviewData.productionRating ?? 0) > 0 ||
			(reviewData.logisticsRating ?? 0) > 0 ||
			(reviewData.foodRating ?? 0) > 0 ||
			Boolean(reviewData.content?.trim())
		);
	}

	// Handle both camelCase and snake_case formats for set/show reviews
	const rating = reviewData.rating ?? 0;
	const content = reviewData.content || null;
	const loved = reviewData.loved || false;
	const sawLive =
		("sawLive" in reviewData ? reviewData.sawLive : false) ||
		("saw_live" in reviewData ? reviewData.saw_live : false) ||
		false;
	const sawStream =
		("sawStream" in reviewData ? reviewData.sawStream : false) ||
		("saw_stream" in reviewData ? reviewData.saw_stream : false) ||
		false;
	const wantToSee =
		("wantToSee" in reviewData ? reviewData.wantToSee : false) ||
		("want_to_see" in reviewData ? reviewData.want_to_see : false) ||
		false;
	const mustSee =
		("mustSee" in reviewData ? reviewData.mustSee : false) ||
		("must_see" in reviewData ? reviewData.must_see : false) ||
		false;

	// A review has meaningful data if ANY of these conditions are true:
	return (
		rating > 0 || // Has star rating
		(content && content.trim().length > 0) || // Has written content
		loved || // Is loved (heart)
		sawLive || // Saw live
		sawStream || // Saw stream
		wantToSee || // Want to see
		mustSee // Must see
	);
}

/**
 * Check if review data is empty (should be deleted)
 * This is an alias for backward compatibility with mobile code
 *
 * @param reviewData - Review data to check
 * @returns true if review is empty and should be deleted
 */
export function isEmptyReviewData(
	reviewData: ReviewDataUnion | null | undefined,
): boolean {
	return !hasReviewData(reviewData);
}

/**
 * Determines if review data is completely empty and should trigger deletion
 * This is the inverse of hasReviewData() and should be used for deletion logic.
 *
 * @param reviewData - Review data to check
 * @returns true if review is empty and should be deleted
 */
export function isEmptyReview(
	reviewData: ReviewDataUnion | null | undefined,
): boolean {
	return !hasReviewData(reviewData);
}

/**
 * Checks if review data has only scheduling flags (want/must see) but no actual review content
 * Used to differentiate between "schedule items" vs "actual reviews"
 *
 * @param reviewData - Review data to check
 * @returns true if only has scheduling flags, false if has review content
 */
export function isScheduleOnly(
	reviewData: ReviewDataUnion | null | undefined,
): boolean {
	if (!reviewData) return false;

	// Festival reviews don't have schedule flags
	if ('overallRating' in reviewData || 'vibeRating' in reviewData) {
		return false;
	}

	const rating = reviewData.rating ?? 0;
	const content = reviewData.content || null;
	const loved = reviewData.loved || false;
	const sawLive =
		("sawLive" in reviewData ? reviewData.sawLive : false) ||
		("saw_live" in reviewData ? reviewData.saw_live : false) ||
		false;
	const sawStream =
		("sawStream" in reviewData ? reviewData.sawStream : false) ||
		("saw_stream" in reviewData ? reviewData.saw_stream : false) ||
		false;
	const wantToSee =
		("wantToSee" in reviewData ? reviewData.wantToSee : false) ||
		("want_to_see" in reviewData ? reviewData.want_to_see : false) ||
		false;
	const mustSee =
		("mustSee" in reviewData ? reviewData.mustSee : false) ||
		("must_see" in reviewData ? reviewData.must_see : false) ||
		false;

	// Has scheduling flags but no review content
	const hasScheduleFlags = wantToSee || mustSee;
	const hasReviewContent =
		rating > 0 ||
		(content && content.trim().length > 0) ||
		loved ||
		sawLive ||
		sawStream;

	return hasScheduleFlags && !hasReviewContent;
}

/**
 * Transform review data for server mutation
 * Ensures consistent format for database operations
 *
 * @param reviewData - Review data to transform
 * @param isEmpty - Whether the review should be marked as empty (for deletion)
 * @returns Transformed review data ready for mutation
 */
export function transformReviewDataForMutation(
	reviewData: any,
	isEmpty: boolean,
) {
	// Common transformation logic
	const baseTransform = {
		content: reviewData.content?.trim() || "",
	};

	// Festival review specific
	if ('overallRating' in reviewData || 'vibeRating' in reviewData) {
		return {
			...baseTransform,
			overallRating: isEmpty ? -2 : reviewData.overallRating || 0,
			vibeRating: isEmpty ? -2 : reviewData.vibeRating || 0,
			productionRating: isEmpty ? -2 : reviewData.productionRating || 0,
			logisticsRating: isEmpty ? -2 : reviewData.logisticsRating || 0,
			foodRating: isEmpty ? -2 : reviewData.foodRating || 0,
		};
	}

	// Set/Show review specific
	return {
		...baseTransform,
		rating: isEmpty ? -2 : reviewData.rating || 0,
		loved: reviewData.loved || false,
		sawLive: reviewData.sawLive || reviewData.saw_live || false,
		sawStream: reviewData.sawStream || reviewData.saw_stream || false,
		wantToSee: reviewData.wantToSee || reviewData.want_to_see || false,
		mustSee: reviewData.mustSee || reviewData.must_see || false,
	};
}